/**
 * Console operator accounts. Super-admin only — the route guard is in App.tsx
 * and the backend refuses the endpoints outright for a normal admin, so this
 * page never has to be the only thing standing between the two roles.
 */
import { useState } from 'react';
import { Loader2, Plus, ShieldCheck } from 'lucide-react';
import { DashboardLayout } from '@/components/dashboard-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmModal } from '@/features/vendors/components/reason-modal';
import { ApiError } from '@/lib/api';
import { use_auth_store, type AdminRole } from '@/lib/auth-store';
import { string_param, use_url_state } from '@/hooks/use-url-state';
import { DateRangeFilter } from '@/components/filters/date-range-filter';
import { ADMIN_ROLE_OPTIONS, type AdminAccount } from '@/features/admins/types';
import { use_admin_accounts, use_create_admin, use_set_admin_active } from '@/features/admins/hooks';

function fmt(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

export function AdminListPage() {
  const me = use_auth_store((s) => s.user);
  const [q, set_q] = use_url_state('q', string_param);
  const [from, set_from] = use_url_state('from', string_param);
  const [to, set_to] = use_url_state('to', string_param);
  const [open, set_open] = useState(false);

  const [email, set_email] = useState('');
  const [password, set_password] = useState('');
  const [first_name, set_first_name] = useState('');
  const [last_name, set_last_name] = useState('');
  const [role, set_role] = useState<AdminRole>('ADMIN');
  const [form_error, set_form_error] = useState<string | null>(null);

  const [pending, set_pending] = useState<AdminAccount | null>(null);
  const [action_error, set_action_error] = useState<string | null>(null);

  const { data, isLoading, error } = use_admin_accounts(q, from || undefined, to || undefined);
  const create_m = use_create_admin();
  const set_active_m = use_set_admin_active();

  function reset_form() {
    set_email('');
    set_password('');
    set_first_name('');
    set_last_name('');
    set_role('ADMIN');
    set_form_error(null);
  }

  function submit() {
    set_form_error(null);
    if (!email.trim()) return set_form_error('An email address is required.');
    if (!first_name.trim()) return set_form_error('A first name is required.');
    create_m.mutate(
      {
        email: email.trim(),
        password,
        first_name: first_name.trim(),
        last_name: last_name.trim() || undefined,
        role,
      },
      {
        onSuccess: () => {
          reset_form();
          set_open(false);
        },
        // The backend spells out exactly which password rule failed; showing its
        // message beats inventing a vaguer one here.
        onError: (err) =>
          set_form_error(err instanceof ApiError ? err.message : 'Could not create this admin.'),
      },
    );
  }

  return (
    <DashboardLayout title="Admins" subtitle="Who can sign in to this console">
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-5">
          <div className="grid gap-1.5">
            <Label htmlFor="admin-search">Search</Label>
            <Input
              id="admin-search"
              data-testid="admin-search"
              placeholder="Name or email"
              value={q}
              onChange={(e) => set_q(e.target.value)}
              className="h-12 w-64"
            />
          </div>
          {/* Added with the rest of the console's date filters — this list had none. */}
          <DateRangeFilter
            from={from}
            to={to}
            on_change={(next) => {
              set_from(next.from);
              set_to(next.to);
            }}
            label="Joined"
          />
          <div className="ml-auto">
            <Button data-testid="admin-new" onClick={() => set_open((v) => !v)}>
              <Plus className="mr-1 h-4 w-4" /> New admin
            </Button>
          </div>
        </CardContent>
      </Card>

      {open && (
        <Card>
          <CardContent className="grid gap-4 p-5 md:grid-cols-2" data-testid="admin-form">
            <div className="grid gap-1.5">
              <Label htmlFor="a-first">First name</Label>
              <Input id="a-first" data-testid="admin-first-name" value={first_name} onChange={(e) => set_first_name(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="a-last">Last name</Label>
              <Input id="a-last" data-testid="admin-last-name" value={last_name} onChange={(e) => set_last_name(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="a-email">Email</Label>
              <Input id="a-email" type="email" data-testid="admin-email" value={email} onChange={(e) => set_email(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="a-password">Password</Label>
              <Input
                id="a-password"
                type="password"
                data-testid="admin-password"
                value={password}
                onChange={(e) => set_password(e.target.value)}
                placeholder="At least 12 characters"
              />
              <p className="text-xs text-muted-foreground">
                12+ characters with an uppercase letter, a lowercase letter and a number. Share it
                with them directly — it is not emailed.
              </p>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="a-role">Role</Label>
              <select
                id="a-role"
                data-testid="admin-role"
                className="h-11 rounded-md border border-input bg-background px-3 text-sm"
                value={role}
                onChange={(e) => set_role(e.target.value as AdminRole)}
              >
                {ADMIN_ROLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label} — {o.hint}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={submit} disabled={create_m.isPending} data-testid="admin-create">
                {create_m.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                Create admin
              </Button>
              <Button variant="outline" onClick={() => { reset_form(); set_open(false); }}>
                Cancel
              </Button>
            </div>
            {form_error && (
              <p className="md:col-span-2 text-sm text-destructive" data-testid="admin-form-error">
                {form_error}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {error ? (
            <div className="p-6 text-sm text-destructive" data-testid="admin-list-error">
              Couldn't load admin accounts.
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center p-12 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !data || data.items.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground" data-testid="admin-list-empty">
              No admin accounts match that search.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-list-table">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Email</th>
                    <th className="px-4 py-3 font-semibold">Role</th>
                    <th className="px-4 py-3 font-semibold">Last sign-in</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((a) => {
                    const is_me = a.id === me?.id;
                    return (
                      <tr key={a.id} className="border-t" data-testid={`admin-row-${a.id}`}>
                        <td className="px-4 py-3 font-medium">
                          {[a.first_name, a.last_name].filter(Boolean).join(' ') || '—'}
                          {is_me && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{a.email ?? '—'}</td>
                        <td className="px-4 py-3">
                          {a.role === 'SUPER_ADMIN' ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                              <ShieldCheck className="h-3 w-3" /> Super admin
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">Admin</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{fmt(a.last_login_at)}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${a.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-muted text-muted-foreground'}`}
                          >
                            {a.is_active ? 'Active' : 'Deactivated'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`admin-toggle-${a.id}`}
                            // Deactivating yourself is refused by the backend and
                            // would be unrecoverable from here; don't offer it.
                            disabled={is_me && a.is_active}
                            onClick={() => {
                              set_action_error(null);
                              set_pending(a);
                            }}
                          >
                            {a.is_active ? 'Deactivate' : 'Reactivate'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmModal
        open={pending !== null}
        title={pending?.is_active ? 'Deactivate this admin?' : 'Reactivate this admin?'}
        description={
          pending?.is_active
            ? `${pending?.email ?? 'They'} will be signed out and refused at login until reactivated.`
            : `${pending?.email ?? 'They'} will be able to sign in to the console again.`
        }
        confirm_label={
          set_active_m.isPending ? 'Saving…' : pending?.is_active ? 'Deactivate' : 'Reactivate'
        }
        loading={set_active_m.isPending}
        error={action_error}
        on_cancel={() => {
          set_pending(null);
          set_action_error(null);
        }}
        on_confirm={() => {
          if (!pending) return;
          set_active_m.mutate(
            { id: pending.id, is_active: !pending.is_active },
            {
              onSuccess: () => set_pending(null),
              onError: (err) =>
                set_action_error(
                  err instanceof ApiError ? err.message : 'Could not update this admin.',
                ),
            },
          );
        }}
      />
    </DashboardLayout>
  );
}
